"use client";
import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import Logo from "../../../../public/assets/melco-logo.webp";
import { usePathname } from "next/navigation";
import { MenuItems } from "../MenuItem";

// Custom SVG Icons
const MenuIcon = () => (
  <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const ChevronDownIcon = ({ className = "" }: { className?: string }) => (
  <svg className={`w-5 h-5 fill-current ${className}`} viewBox="0 0 24 24">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
    <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

// Helper for Links
const SmartLink = ({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  const isExternal = href.startsWith("http");
  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
};

const MobileHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | number | null>(null);
  const pathname = usePathname();

  const handleDrawerClose = () => setDrawerOpen(false);

  const toggleSubMenu = (id: string | number) => {
    setExpandedMenu((prev) => (prev === id ? null : id));
  };

  return (
    <>
      {/* Sticky Mobile Top Navigation Bar */}
      <nav className="sticky top-0 w-full z-40 bg-[#343a40] border-b border-gray-700/50 shadow-md px-4 py-3 flex items-center justify-between">
        <Link href="/" className="flex items-center">
          <Image
            src={Logo}
            alt="Logo"
            width={140}
            height={40}
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Menu Toggle Button */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="text-white hover:text-[#ffb400] p-1.5 rounded-md focus:outline-none transition-colors"
          aria-label="Open Navigation Menu"
        >
          <MenuIcon />
        </button>
      </nav>

      {/* Backdrop Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-50 transition-opacity"
          onClick={handleDrawerClose}
        />
      )}

      {/* Sliding Side Drawer Navigation */}
      <div
        className={`fixed top-0 right-0 h-full w-[85%] max-w-[320px] bg-[#2b3035] text-white z-50 shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer Header */}
        <div className="bg-[#343a40] p-4 flex items-center justify-between border-b border-gray-700">
          <Link href="/" onClick={handleDrawerClose}>
            <Image
              src={Logo}
              alt="Logo"
              width={120}
              height={35}
              className="h-8 w-auto object-contain"
            />
          </Link>
          <button
            onClick={handleDrawerClose}
            className="text-gray-300 hover:text-[#ffb400] p-1 rounded-md transition-colors"
            aria-label="Close Navigation Menu"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Menu Items List */}
        <div className="flex-1 overflow-y-auto py-2">
          <ul className="flex flex-col divide-y divide-gray-700/50">
            {MenuItems.map((item) => {
              const isActive = pathname === item.href;
              const isExpanded = expandedMenu === item.id;

              return (
                <li key={item.id} className="w-full">
                  <div className="flex justify-between items-center px-4 py-3.5 hover:bg-[#343a40] transition">
                    <SmartLink
                      href={item.href ?? "#"}
                      onClick={handleDrawerClose}
                      className={`text-sm font-bold tracking-wide flex items-center gap-2 transition-colors ${
                        isActive ? "text-[#ffb400]" : "text-gray-100"
                      }`}
                    >
                      <span>{item.name}</span>
                      {item.badge && (
                        <span className="bg-[#ffb400] text-black font-extrabold text-[9px] px-1.5 py-0.5 rounded uppercase">
                          {item.badge}
                        </span>
                      )}
                    </SmartLink>

                    {item.submenu && (
                      <button
                        onClick={() => toggleSubMenu(item.id)}
                        className="text-gray-400 hover:text-[#ffb400] p-1 focus:outline-none"
                        aria-label="Toggle submenu"
                      >
                        <ChevronDownIcon
                          className={`transition-transform duration-200 ${
                            isExpanded ? "rotate-180 text-[#ffb400]" : ""
                          }`}
                        />
                      </button>
                    )}
                  </div>

                  {/* Accordion Submenu */}
                  {item.submenu && isExpanded && (
                    <ul className="bg-[#212529] border-l-2 border-[#ffb400] my-1">
                      {item.submenu.map((sub) => (
                        <li key={`sub-${sub.id}`}>
                          <SmartLink
                            href={sub.href}
                            onClick={handleDrawerClose}
                            className="text-xs px-6 py-2.5 block text-gray-300 hover:text-[#ffb400] hover:bg-[#2b3035] transition"
                          >
                            {sub.name}
                          </SmartLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Drawer Footer Contact Info */}
        <div className="bg-[#343a40] p-4 border-t border-gray-700 space-y-2 text-xs text-gray-300">
          <a
            href="tel:+511234567"
            className="flex items-center gap-2 hover:text-[#ffb400] transition"
          >
            <PhoneIcon />
            <span>+51 123 4567</span>
          </a>
          <a
            href="mailto:contact@email.com"
            className="flex items-center gap-2 hover:text-[#ffb400] transition"
          >
            <MailIcon />
            <span>contact@email.com</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default MobileHeader;